/**
 * Activity — design/PROTOTYPE-SPEC.md §9.
 *
 * "Every in/out/disposal with staff, filterable by product/type, searchable;
 * void any entry (stock auto-corrects); lot-number trace."
 *
 * The ledger is append-only, so this is a pure read and always tells the truth
 * about what happened. A void is not an edit: it inserts a reversing row, which
 * is why both the original and its correction appear here, the original struck
 * through. Hiding the mistake would make the ledger a summary rather than a
 * record — see `modules/movements.ts`.
 *
 * Lot trace is the search box: type a lot number and you get every movement that
 * ever touched it. That is the recall question — "where did this batch go?" —
 * and it is the reason batch ids live on movements at all.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { listMovements, voidMovement, type MovementRow } from '../lib/api.js';
import { c, field, segTab, segTrack } from '../theme.js';
import { cardStyle, formatMovementWhen, humaniseReason, money, smallButton } from './format.js';

type TypeFilter = 'all' | 'in' | 'out' | 'waste';

const TYPE_LABEL: Record<MovementRow['type'], string> = {
  in: 'Received',
  out: 'Used',
  waste: 'Written off',
  adjustment: 'Adjusted',
};

const TYPE_TONE: Record<MovementRow['type'], string> = {
  in: c.success,
  out: c.ink,
  waste: c.danger,
  adjustment: c.green,
};

export function ActivityPage({
  onChanged,
  refreshToken,
}: {
  onChanged: () => void;
  refreshToken: number;
}) {
  const [rows, setRows] = useState<MovementRow[]>([]);
  const [type, setType] = useState<TypeFilter>('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (type !== 'all') params.set('type', type);
      setRows((await listMovements(`?${params.toString()}`)).movements);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load activity');
    } finally {
      setLoading(false);
    }
    // refreshToken is a dependency, not a value — see AppShell's header note.
  }, [type, refreshToken]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Searching filters what was fetched rather than re-querying. The API has no
   * text search on movements, and adding one to filter 200 rows the browser
   * already holds would be a round trip for nothing.
   */
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((m) =>
      `${m.productName ?? ''} ${m.lot ?? ''} ${m.reason ?? ''} ${m.note ?? ''} ${m.byName ?? ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  async function undo(m: MovementRow) {
    setBusyId(m.id);
    setError(null);
    try {
      const res = await voidMovement(m.id);
      setFlash(
        `Voided ${TYPE_LABEL[m.type].toLowerCase()} of ${res.qty} · stock corrected`,
      );
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not void that entry');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div data-tour="movements">
      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}
      {flash && (
        <div
          style={{
            background: c.okBg,
            border: `1px solid ${c.okLine}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.success,
          }}
        >
          {flash}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={segTrack}>
        {(
          [
            ['all', 'All'],
            ['in', 'Received'],
            ['out', 'Used'],
            ['waste', 'Written off'],
          ] as [TypeFilter, string][]
        ).map(([key, lbl]) => (
          <button
            key={key}
            type="button"
            onClick={() => setType(key)}
            style={segTab(type === key)}
          >
            {lbl}
          </button>
        ))}
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Trace a lot number, product or person"
          style={{ ...field, maxWidth: 320, marginLeft: 'auto' }}
        />
      </div>

      {loading ? (
        <p style={{ color: c.muted, fontSize: 14 }}>Loading…</p>
      ) : visible.length === 0 ? (
        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 13.5, color: c.muted }}>
            {rows.length === 0
              ? 'Nothing has moved yet. Booking stock in or logging use will show up here.'
              : `Nothing matches “${search.trim()}”.`}
          </p>
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr>
                  {['When', 'What', 'Product', 'Lot', 'Qty', 'By', ''].map((h, i) => (
                    <th
                      key={h || i}
                      style={{
                        textAlign: 'left',
                        padding: '11px 16px',
                        fontSize: 12.5,
                        textTransform: 'lowercase',
                        color: c.label,
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr
                    key={m.id}
                    style={{
                      borderTop: `1px solid ${c.border}`,
                      opacity: m.isVoided ? 0.5 : 1,
                    }}
                  >
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap', color: c.muted2 }}>
                      {formatMovementWhen(m.occurredOn, m.createdAt)}
                    </td>
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          fontWeight: 500,
                          color: TYPE_TONE[m.type],
                          textDecoration: m.isVoided ? 'line-through' : 'none',
                        }}
                      >
                        {TYPE_LABEL[m.type]}
                      </span>
                      {m.voidsMovementId && (
                        <span style={{ fontSize: 11, color: c.green, marginLeft: 7 }}>
                          correction
                        </span>
                      )}
                      {/*
                        The reason only earns its line when it says something the
                        type has not: a `received` receipt would otherwise read
                        "Received / Received", whereas `stock_count` tells you
                        which of several things produced this row.
                      */}
                      {m.reason && humaniseReason(m.reason) !== TYPE_LABEL[m.type] && (
                        <div style={{ fontSize: 12, color: c.faint, marginTop: 2 }}>
                          {humaniseReason(m.reason)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '11px 16px' }}>{m.productName ?? '—'}</td>
                    <td style={{ padding: '11px 16px', color: c.muted2 }}>{m.lot ?? '—'}</td>
                    <td style={{ padding: '11px 16px', fontVariantNumeric: 'tabular-nums' }}>
                      {m.type === 'in' ? '+' : '−'}
                      {m.qty}
                      {m.pricePence !== null && (
                        <div style={{ fontSize: 12, color: c.faint, marginTop: 2 }}>
                          {money(m.pricePence)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '11px 16px', color: c.muted2 }}>{m.byName ?? '—'}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                      {/* A reversal is itself a movement and cannot be voided —
                          a correction of a correction is a redo dressed as an undo. */}
                      {!m.isVoided && !m.voidsMovementId && (
                        <button
                          type="button"
                          disabled={busyId === m.id}
                          onClick={() => void undo(m)}
                          style={smallButton}
                        >
                          {busyId === m.id ? '…' : 'Void'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
